// Central, documented registry mapping the "Report Information" tables'
// row labels to the LSDB API fields that should populate them, keyed by a
// normalized (case/punctuation/space-insensitive) version of the label.
//
// Different Report Types ship with their own Word-doc-derived template
// table containing whichever rows are relevant to that report type (e.g.
// "Document Number" may exist for one report type and not another, or a
// report type may add extra rows). Rather than hardcoding one fixed table
// shape, LSDBButtonView matches labels dynamically against the alias maps
// below: any row whose label matches (once normalized) gets auto-populated,
// regardless of which report/template it came from or how many rows/
// columns that particular report type's table has. This means the same
// fetch-and-populate logic supports every current — and future — report
// type's table layout automatically, as long as its labels are represented
// here.
//
// To support a NEW field for some report type, add one alias entry to the
// relevant map below with a getter that pulls the right value off the LSDB
// API response item. No other code needs to change.

/**
 * Normalizes a table cell's label text so it can be matched regardless of
 * case, trailing punctuation (periods/colons), extra whitespace, or minor
 * spelling variants coming from Word-doc-converted templates (e.g.
 * "Project Number." and "project number" both normalize to the same key).
 */
export const normalizeKey = (str: string): string => {
  if (!str) return "";
  return str
    .toLowerCase()
    .replace(/\[.*?\]|\(.*?\)/g, "")
    .replace(/category/g, "catagory")
    .replace(/[^a-z0-9]/g, "");
};

const formatDate = (isoStr: string): string => {
  if (!isoStr) return "";
  try {
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return isoStr;
  }
};

export type FieldAliasMap = Record<string, string>;

/**
 * "Report Details" key-value grid: Report Number / Project Number / Date of
 * Issue / Customer / Customer Contact / Document Number. Add further
 * entries here as additional report types introduce new fields.
 */
export const buildReportDetailsFieldAliases = (apiItem: any): FieldAliasMap => ({
  [normalizeKey("report number")]:
    apiItem["report Number"] || apiItem.report_number || "",
  [normalizeKey("project number")]: apiItem.project_number || "",
  [normalizeKey("date of issue")]: formatDate(apiItem.date_of_issue),
  [normalizeKey("customer")]: apiItem.customer_name || "",
  [normalizeKey("customer contact")]: apiItem.customer_contact || "",
  [normalizeKey("document number")]: apiItem.document_number || "",
});

/** Detects whether a table's rows match the "Report Details" layout. */
export const isReportDetailsTemplate = (rowTexts: string[][]): boolean => {
  const text = rowTexts
    .map((r) => r.join(" "))
    .join(" ")
    .toLowerCase();
  return text.includes("report number") && text.includes("customer");
};

/**
 * "Report Approval" grid: Preparation / Verification / Approval signer
 * names.
 */
export const buildReportApprovalFieldAliases = (apiItem: any): FieldAliasMap => ({
  [normalizeKey("preparation")]: apiItem.report_writer_name || "",
  [normalizeKey("verification")]: apiItem.report_reviewer_name || "",
  [normalizeKey("approval")]: apiItem.report_approver_name || "",
});

/** Detects whether a table's rows match the "Report Approval" layout. */
export const isReportApprovalTemplate = (fullTableText: string): boolean => {
  return (
    fullTableText.includes("preparation") &&
    fullTableText.includes("verification")
  );
};
